import React, { useState, useEffect, useMemo } from 'react';
import { Check, Lock, X, Plus, Ban, ChevronDown } from 'lucide-react';
import { colors, FONT } from '../../../theme';
import { S } from '../dashboardStyles';
import { SS } from './settingsStyles';
import SettingsScreen from './SettingsScreen';
import { publicOrigin, isEmbeddedShell } from '../../../services/serverBase.mjs';
import {
  serverList, addCustomServer, removeCustomServer, readCustomServers, writeCustomServers,
  canSwitchServer, switchServer,
} from '../../../services/serverList.mjs';
import { currentRelayList } from '../../../services/relayConfig.mjs';
import { secureSyncStorage } from '../../../services/secureStore.mjs';

/**
 * Where the app finds its server — shown, not assumed.
 *
 * Until now the address lived in a first-run dialog and nowhere else: to
 * move a phone to CORE under its own name (SEC-ARCH-003) meant reinstalling.
 * The list has two parts with two different owners. The system part is the
 * binary's: chosen, never edited, because an address that can be edited on
 * this screen can be edited by anyone who reaches it. The custom part is the
 * person's: relays they were handed, servers of their own.
 *
 * Choosing an entry hands the address to the shell, which restarts the
 * embedded UI against it. That is a restart of the app, said as such before
 * it happens; a different server may also mean a different account.
 */
export default function ServersScreen({ onBack }) {
  const active = publicOrigin();
  const switchable = canSwitchServer();
  const [custom, setCustom] = useState(() => readCustomServers(safeStorage()));
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [switching, setSwitching] = useState(null);

  const list = serverList({ active, custom });

  const persist = (next) => {
    setCustom(next);
    try { writeCustomServers(safeStorage(), next); } catch (e) { /* private mode: list lives for the session */ }
  };

  const add = () => {
    setError('');
    const r = addCustomServer(custom, draft);
    if (!r.ok) { setError(r.reason); return; }
    persist(r.custom);
    setDraft('');
  };

  const remove = (url) => {
    setError('');
    const r = removeCustomServer(custom, url, active);
    if (!r.ok) { setError(r.reason); return; }
    persist(r.custom);
  };

  const doSwitch = (url) => {
    setError('');
    if (!switchServer(url)) { setError('Переключение доступно только в приложении'); return; }
    setSwitching(url);
    setConfirming(null);
  };

  const row = (e, i, n) => (
    <div key={e.url} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', minHeight: 56,
      borderBottom: i < n - 1 ? `1px solid ${colors.gray100}` : 'none',
      background: e.active ? colors.gray100 : 'transparent',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: colors.gray900, display: 'flex', alignItems: 'center', gap: 6 }}>
          {e.label}
          {e.system && <Lock size={13} color={colors.gray400} />}
        </div>
        <div style={{ fontSize: 12, color: colors.gray600, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.url}</div>
        {confirming === e.url && (
          <div style={{ marginTop: 8 }}>
            <div style={{ ...SS.consequence, marginBottom: 8 }}>
              Приложение перезапустится с этим адресом. Если это другой сервер, понадобится войти заново.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => doSwitch(e.url)} style={{ ...S.primaryBtn, padding: '8px 14px', fontSize: 14 }}>Переключиться</button>
              <button onClick={() => setConfirming(null)} style={{ ...S.settingsAction, width: 'auto', padding: '8px 10px', fontSize: 14, color: colors.gray600 }}>Отмена</button>
            </div>
          </div>
        )}
      </div>
      {e.active
        ? <Check size={18} color={colors.gray900} style={{ flexShrink: 0 }} />
        : switchable && confirming !== e.url && (
          <button onClick={() => setConfirming(e.url)} disabled={!!switching}
            style={{ ...S.settingsAction, width: 'auto', padding: '6px 10px', fontSize: 14, color: colors.gray900, flexShrink: 0 }}>
            Выбрать
          </button>
        )}
      {e.removable && !switching && (
        <button onClick={() => remove(e.url)} aria-label="Убрать"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, flexShrink: 0 }}>
          <X size={16} color={colors.gray400} />
        </button>
      )}
    </div>
  );

  const system = list.filter((e) => e.system);
  const own = list.filter((e) => !e.system);

  return (
    <SettingsScreen title="Сервер и реле" onBack={onBack} scrollKey="servers">
      <div style={{ ...SS.consequence, padding: '0 16px 10px' }}>
        Адрес, по которому приложение находит ваш сервер: сюда идут сообщения, звонки и вложения.
        Системные адреса вшиты в приложение и не редактируются. Свои — реле или другой сервер — добавляются вручную.
      </div>
      {!isEmbeddedShell() && (
        <div style={{ ...SS.consequence, padding: '0 16px 10px' }}>
          В браузере адрес сервера — это адрес открытой страницы. Переключение доступно в приложении.
        </div>
      )}
      {switching && (
        <div style={{ ...SS.consequence, padding: '0 16px 10px', color: colors.gray900 }}>
          Переключаю на {switching}…
        </div>
      )}
      {error && (
        <div style={{ ...SS.consequence, color: colors.red, padding: '0 16px 10px' }}>{error}</div>
      )}

      {/* The signed list of paths to CORE (SEC-ARCH-002): shown so a person
          can see which version the phone holds and until when. Using it for
          connections is SEC-ARCH-006. */}
      <div style={S.sectionLabel}>Пути к серверу</div>
      <div style={S.settingsCard}>
        <RelayListStatus />
      </div>

      <div style={S.sectionLabel}>Системные</div>
      <div style={S.settingsCard}>{system.map((e, i) => row(e, i, system.length))}</div>

      <div style={S.sectionLabel}>Свои</div>
      <div style={S.settingsCard}>
        {own.length === 0 && (
          <div style={{ ...SS.consequence, padding: '14px 16px' }}>Пока ни одного своего адреса.</div>
        )}
        {own.map((e, i) => row(e, i, own.length))}
      </div>

      <div style={S.settingsCard}>
        <div style={{ display: 'flex', gap: 8, padding: 12 }}>
          <input value={draft} onChange={(ev) => setDraft(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') add(); }}
            placeholder="relay.example.com" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            inputMode="url" style={{ ...S.input, flex: 1, minWidth: 0 }} />
          <button onClick={add} disabled={!draft.trim()}
            style={{ ...S.primaryBtn, display: 'flex', alignItems: 'center', gap: 6, opacity: draft.trim() ? 1 : 0.5 }}>
            <Plus size={16} /> Добавить
          </button>
        </div>
        <div style={{ ...SS.consequence, padding: '0 16px 12px', fontFamily: FONT }}>
          Только адрес: https://хост или хост:порт. Без пути.
        </div>
      </div>
    </SettingsScreen>
  );
}

// ─── Пути к серверу: grouped inset list ─────────────────────────────────────
// One row per host, ports folded under it, a fixed 80 px traffic column that
// can never push the row wider. Green means a pipe is open or bytes moved
// within the last poll — chosen from the counters, not from what the proxy
// remembers, so the highlight is honest and stable.

// Mirrors theme.MONO (not imported in this file today).
const MONO_FONT = "ui-monospace, 'SF Mono', 'Menlo', 'Roboto Mono', monospace";
// A counter that climbed within this window is "moving": long enough to be
// seen between 1 s polls, short enough to fall back once the pipe idles.
const MOVING_MS = 2500;
// Bytes moved within this window but no pipe now: "recent" — a green ring, so
// the path that just carried a burst does not vanish into grey the next poll.
const RECENT_MS = 15000;
// A rate this small is EMA decay, not traffic.
const RATE_FLOOR = 1;
// Hosts beyond this many are folded when they carried nothing.
const FOLD_AT = 8;

const RS = {
  hairline: `1px solid ${colors.gray200}`,
  // One host = one row, ≈54 px like an iOS two-line cell.
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', minHeight: 54, fontFamily: FONT },
  dotCol: { width: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 15, lineHeight: '20px', minWidth: 0 },
  titleText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  kind: { fontSize: 12, color: colors.gray500, flexShrink: 0, whiteSpace: 'nowrap' },
  badge: {
    fontSize: 11, fontWeight: 600, lineHeight: '16px', padding: '0 6px', borderRadius: 9, flexShrink: 0,
    color: colors.green, background: colors.greenLight, border: `1px solid ${colors.greenBorder}`,
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  },
  // Secondary line: host (ellipsised) + ports; ports drop to their own line
  // when the two do not fit — 20 relays stay two or three lines each.
  sub: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 10, rowGap: 2, marginTop: 1, fontSize: 12, lineHeight: '16px', minWidth: 0 },
  host: { fontFamily: MONO_FONT, color: colors.gray500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, maxWidth: '100%' },
  ports: { display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  port: { display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: MONO_FONT, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  portDot: { width: 5, height: 5, borderRadius: 3, background: colors.greenDot, flexShrink: 0 },
  // Live meter: bytes per second from poll-to-poll deltas, tabular so the
  // digits tick in place.
  meter: { fontFamily: MONO_FONT, fontSize: 12, lineHeight: '16px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // Trailing traffic column: fixed width, right-aligned, tabular — it can
  // never push the row wider, whatever the number.
  traffic: {
    width: 80, flexShrink: 0, textAlign: 'right', fontFamily: MONO_FONT, fontSize: 12, lineHeight: '16px',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden',
  },
  groupHeader: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8,
    padding: '10px 16px 4px', fontSize: 12, fontWeight: 600, color: colors.gray500, fontFamily: FONT,
  },
  // iOS-style switch, built from two spans so no new dependency is needed.
  switchRow: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 56, padding: '10px 16px',
    background: 'transparent', border: 'none', borderBottom: `1px solid ${colors.gray200}`,
    textAlign: 'left', cursor: 'pointer', fontFamily: FONT, color: colors.gray900,
  },
  track: { width: 44, height: 26, borderRadius: 13, padding: 2, boxSizing: 'border-box', flexShrink: 0, display: 'block', transition: 'background 160ms' },
  knob: { display: 'block', width: 22, height: 22, borderRadius: 11, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.4)', transition: 'transform 160ms' },
  foldBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%', minHeight: 44,
    padding: '10px 16px', background: 'transparent', border: 'none', borderTop: `1px solid ${colors.gray200}`,
    cursor: 'pointer', fontFamily: FONT, fontSize: 13, color: colors.gray600,
  },
};

function RelayListStatus() {
  const [relayOnly, setRelayOnly] = useState(() => { try { return secureSyncStorage.getItem('voxiox_relay_only') === '1'; } catch (e) { return false; } });
  const [expanded, setExpanded] = useState(false);
  // data: the proxy's JSON (or null); at: ms of that poll; movedAt:
  // "<host:port>/up|down" → ms of the last poll on which that counter climbed;
  // rates: "<host:port>" → { up, down } bytes/s, an EMA of poll-to-poll deltas.
  // Kept together so one functional update sees the previous snapshot.
  const [snap, setSnap] = useState({ data: null, at: 0, movedAt: {}, rates: {} });
  useEffect(() => {
    // The native proxy reports which path it took last; the WebView cannot
    // see it otherwise — that is the point of the design.
    const read = () => {
      let data = null;
      try { const raw = window.VoxioxNative?.relayStatus?.(); data = raw ? JSON.parse(raw) : null; } catch (e) { data = null; }
      const now = Date.now();
      setSnap((prev) => {
        const movedAt = { ...prev.movedAt };
        const rates = {};
        const before = (prev.data && prev.data.paths) || {};
        const after = (data && data.paths) || {};
        const dt = prev.at ? Math.max(0.5, (now - prev.at) / 1000) : 0;
        for (const [key, p] of Object.entries(after)) {
          const b = before[key];
          if (!b || !dt) continue;
          const dUp = Math.max(0, (p.up || 0) - (b.up || 0));
          const dDown = Math.max(0, (p.down || 0) - (b.down || 0));
          if (dUp > 0) movedAt[`${key}/up`] = now;
          if (dDown > 0) movedAt[`${key}/down`] = now;
          const r = prev.rates[key] || { up: 0, down: 0 };
          rates[key] = { up: ema(r.up, dUp / dt), down: ema(r.down, dDown / dt) };
        }
        return { data, at: now, movedAt, rates };
      });
    };
    read(); const t = setInterval(read, 1000); return () => clearInterval(t);
  }, []);
  // Two JSON.parse calls per read; the list changes when a new version is
  // adopted, not every second, so it is re-read when the proxy's version moves.
  const listVersion = snap.data && snap.data.version;
  const rl = useMemo(() => { try { return currentRelayList(secureSyncStorage); } catch (e) { return null; } }, [listVersion]);
  if (!rl) return null;

  const status = snap.data;
  const hasProxy = !!(status && status.proxy);
  const paths = (status && status.paths) || {};
  const now = Date.now();
  const movedWithin = (key, dir, ms) => now - (snap.movedAt[`${key}/${dir}`] || 0) < ms;
  const rateOf = (key) => snap.rates[key] || { up: 0, down: 0 };

  // Every host the list names, in the owner's order, ports folded under it,
  // plus whatever else the proxy has carried (SIP on 8089, a custom server).
  const hosts = groupHosts(rl, paths, { movedWithin, rateOf });
  const totals = hosts.reduce((t, g) => ({
    live: t.live + g.live, up: t.up + g.up, down: t.down + g.down,
    upRate: t.upRate + g.upRate, downRate: t.downRate + g.downRate,
    upMoving: t.upMoving || g.upMoving, downMoving: t.downMoving || g.downMoving,
  }), { live: 0, up: 0, down: 0, upRate: 0, downRate: 0, upMoving: false, downMoving: false });

  // The highlighted path is chosen from the data: the open pipe with the
  // highest byte rate, else the port that moved most recently within 15 s.
  // The proxy's remembered path is shown as a memory when nothing is moving.
  const hero = pickHero(hosts);
  const headerState = hero && hero.state === 'active' ? 'active'
    : hero ? 'recent'
    : status && status.lastPath === 'нет пути' ? 'down'
    : totals.up + totals.down > 0 ? 'cooling' : 'idle';
  const headerTitle = hero && hero.state === 'active' ? `Сейчас ${describePath(hero)}`
    : hero ? `Только что ${describePath(hero)}`
    : headerState === 'down' ? 'Нет пути'
    : `Последний путь: ${(status && status.lastPath) || 'нет данных'}`;
  const liveText = status && status.live && Object.keys(status.live).length
    ? Object.entries(status.live).map(([k, n]) => `${k} ×${n}`).join(', ')
    : null;
  const expires = rl.source === 'signed' && rl.expires ? new Date(rl.expires * 1000) : null;

  // Fold: the first hosts always show; beyond that only those that carried
  // bytes — 20 relays fit without dropping any counter that matters.
  const visible = expanded ? hosts : hosts.filter((g, i) => i < FOLD_AT || g.used || g.live > 0);
  const hidden = hosts.length - visible.length;

  const toggle = () => {
    const next = !relayOnly; setRelayOnly(next);
    try { secureSyncStorage.setItem('voxiox_relay_only', next ? '1' : '0'); } catch (e) { /* session only */ }
    // On → the proxy severs the direct pipes already open; a kept-alive
    // connection would otherwise keep going straight for minutes.
    try { window.VoxioxNative?.setRelayOnly?.(next); } catch (e) { /* no proxy */ }
  };

  return (
    <div>
      {/* Сейчас: what the proxy is doing this second, with totals ticking. */}
      {hasProxy ? (
        <div style={{ ...RS.row, borderBottom: RS.hairline, background: headerState === 'active' ? colors.greenLight : 'transparent' }}>
          <div style={RS.dotCol}><StateDot state={headerState} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...RS.title, fontWeight: 600, color: headerState === 'active' || headerState === 'down' ? colors.gray900 : headerState === 'recent' ? colors.gray800 : colors.gray600 }}>
              <span style={RS.titleText}>{headerTitle}</span>
            </div>
            <div style={{ ...SS.consequence, fontSize: 12, lineHeight: '16px', marginTop: 1 }}>
              {liveText ? `Открытые: ${liveText}` : 'Открытых соединений нет'}
              {status.relayOnly ? ' · только через реле' : ''}
            </div>
            <RateLine up={totals.upRate} down={totals.downRate} />
          </div>
          <TrafficCol up={totals.up} down={totals.down} upMoving={totals.upMoving} downMoving={totals.downMoving} tone={headerState} used={totals.up + totals.down > 0} />
        </div>
      ) : (
        <div style={{ ...SS.consequence, padding: '12px 16px', borderBottom: RS.hairline }}>
          Состояние путей видно только в приложении: в браузере прокси нет.
        </div>
      )}

      {hasProxy && (
        <button role="switch" aria-checked={relayOnly} onClick={toggle} style={RS.switchRow}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: colors.gray900, lineHeight: '20px' }}>Только через реле</div>
            <div style={{ ...SS.consequence, fontSize: 12, lineHeight: '16px' }}>Прямой путь не используется — так проверяют реле</div>
          </div>
          <span aria-hidden style={{ ...RS.track, background: relayOnly ? colors.green : colors.gray300 }}>
            <span style={{ ...RS.knob, transform: relayOnly ? 'translateX(18px)' : 'translateX(0)' }} />
          </span>
        </button>
      )}

      <div style={RS.groupHeader}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {rl.source === 'signed' ? `Подписанный список, версия ${rl.version}` : 'Вшитый список (подписанного ещё нет)'}
        </span>
        <span style={{ flexShrink: 0, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
          {hosts.length} {plural(hosts.length, 'адрес', 'адреса', 'адресов')}
        </span>
      </div>

      {hosts.length === 0 && (
        <div style={{ ...SS.consequence, padding: '8px 16px 14px' }}>Список пуст.</div>
      )}
      {visible.map((g, i) => {
        const st = g.direct && relayOnly && g.live === 0 ? 'off' : g.state;
        const label = g.direct ? 'CORE' : (g.name || g.host);
        const hostLine = g.sni || g.host;
        const showHost = hostLine !== label;
        const ink = st === 'active' ? colors.gray900 : st === 'recent' ? colors.gray800 : st === 'cooling' ? colors.gray900 : colors.gray600;
        // gray500 measures 4.1:1 on the green row — below what 12 px needs.
        const dim = st === 'active' ? colors.gray600 : colors.gray500;
        const rate = g.upRate + g.downRate;
        return (
          <div key={g.host} style={{
            ...RS.row,
            background: st === 'active' ? colors.greenLight : 'transparent',
            borderBottom: i < visible.length - 1 ? RS.hairline : 'none',
          }}>
            <div style={RS.dotCol}><StateDot state={st} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={RS.title}>
                <span style={{ ...RS.titleText, fontWeight: st === 'active' ? 600 : 500, color: ink }}>{label}</span>
                <span style={{ ...RS.kind, color: dim }}>{g.direct ? (st === 'off' ? 'напрямую · выключен' : 'напрямую') : 'реле'}</span>
                {st === 'active' && g.live > 0 && <span style={RS.badge}>×{g.live}</span>}
              </div>
              <div style={RS.sub}>
                {showHost && <span style={{ ...RS.host, color: dim }}>{hostLine}</span>}
                <span style={RS.ports}>
                  {g.ports.map((r) => (
                    <span key={r.key} style={{ ...RS.port, color: r.live > 0 ? colors.gray900 : (r.up + r.down > 0 ? colors.gray600 : dim) }}>
                      {r.live > 0 && <span style={RS.portDot} />}
                      {r.port}{r.live > 1 ? `×${r.live}` : ''}
                    </span>
                  ))}
                </span>
                {st === 'active' && rate >= RATE_FLOOR && (
                  <span style={{ ...RS.meter, color: colors.gray900, flexShrink: 0 }}>{fmtRate(rate)}</span>
                )}
              </div>
            </div>
            <TrafficCol up={g.up} down={g.down} upMoving={g.upMoving} downMoving={g.downMoving} tone={st} used={g.used} />
          </div>
        );
      })}

      {hidden > 0 && (
        <button onClick={() => setExpanded(true)} style={RS.foldBtn}>
          Ещё {hidden} без трафика <ChevronDown size={14} />
        </button>
      )}
      {expanded && hosts.length > FOLD_AT && (
        <button onClick={() => setExpanded(false)} style={RS.foldBtn}>Свернуть</button>
      )}

      {expires && (
        <div style={{ ...SS.consequence, padding: '8px 16px 12px', borderTop: RS.hairline }}>
          Действует до {expires.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      )}
    </div>
  );
}

// active: filled green with a soft halo — the one thing on the screen that
// glows. recent: green ring, bytes moved within 15 s but no pipe now.
// cooling: hollow grey ring, "was used earlier". idle: small dim dot.
// off: the direct host while «только через реле» is on. down: no path at all.
function StateDot({ state }) {
  if (state === 'off') return <Ban size={12} color={colors.gray500} strokeWidth={2} />;
  if (state === 'active') return <span style={{ width: 9, height: 9, borderRadius: 5, background: colors.greenDot, boxShadow: `0 0 0 3px ${colors.greenBorder}`, display: 'block' }} />;
  if (state === 'recent') return <span style={{ width: 9, height: 9, borderRadius: 5, border: `1.5px solid ${colors.green}`, boxSizing: 'border-box', display: 'block' }} />;
  if (state === 'down') return <span style={{ width: 9, height: 9, borderRadius: 5, background: colors.red, display: 'block' }} />;
  if (state === 'cooling') return <span style={{ width: 9, height: 9, borderRadius: 5, border: `1.5px solid ${colors.gray500}`, boxSizing: 'border-box', display: 'block' }} />;
  return <span style={{ width: 6, height: 6, borderRadius: 3, background: colors.gray400, display: 'block' }} />;
}

// ↑ and ↓ on two lines in a column that is always 80 px wide. The arrow turns
// green while that direction is moving; a host that never carried a byte
// shows a single dash instead of two zeros.
function TrafficCol({ up, down, upMoving, downMoving, tone, used }) {
  if (!used && tone !== 'active') return <div style={{ ...RS.traffic, color: colors.gray400 }}>—</div>;
  const ink = tone === 'active' ? colors.gray900 : tone === 'recent' ? colors.gray800 : colors.gray600;
  const arrow = (on) => ({ color: on ? colors.green : colors.gray500, fontWeight: on ? 700 : 400 });
  return (
    <div style={{ ...RS.traffic, color: ink }}>
      <div><span style={arrow(upMoving)}>↑</span> {fmtBytes(up)}</div>
      <div><span style={arrow(downMoving)}>↓</span> {fmtBytes(down)}</div>
    </div>
  );
}

// The live meter under «Сейчас»: always present so the row does not jump,
// grey at zero, ink while bytes move.
function RateLine({ up, down }) {
  const u = up >= RATE_FLOOR ? up : 0, d = down >= RATE_FLOOR ? down : 0;
  const on = u > 0 || d > 0;
  return (
    <div style={{ ...RS.meter, marginTop: 1, color: on ? colors.gray900 : colors.gray500 }}>
      <span style={{ color: u > 0 ? colors.green : colors.gray500, fontWeight: u > 0 ? 700 : 400 }}>↑</span> {fmtRate(u)}
      {' · '}
      <span style={{ color: d > 0 ? colors.green : colors.gray500, fontWeight: d > 0 ? 700 : 400 }}>↓</span> {fmtRate(d)}
    </div>
  );
}

// One entry per host: the list's endpoints first, in the owner's order, then
// any host:port the proxy reports that the list does not name. Each host and
// port carries its state, computed once here.
function groupHosts(rl, paths, { movedWithin, rateOf }) {
  const byHost = new Map();
  const group = (h) => {
    if (!byHost.has(h)) byHost.set(h, { host: h, name: '', direct: false, sni: '', ports: [] });
    return byHost.get(h);
  };
  const portRow = (key, port) => {
    const p = paths[key] || {};
    const r = rateOf(key);
    const lastMoved = Math.max(
      movedWithin(key, 'up', RECENT_MS) ? 1 : 0, movedWithin(key, 'down', RECENT_MS) ? 1 : 0,
    );
    return {
      key, port: String(port), live: p.live || 0, up: p.up || 0, down: p.down || 0,
      upMoving: movedWithin(key, 'up', MOVING_MS), downMoving: movedWithin(key, 'down', MOVING_MS),
      upRate: r.up, downRate: r.down, recent: lastMoved > 0,
    };
  };
  for (const e of rl.endpoints || []) {
    if (!e || !e.host) continue;
    const g = group(e.host);
    g.name = g.name || e.name || ''; g.direct = g.direct || !!e.direct; g.sni = g.sni || e.sni || '';
    for (const port of e.ports || []) {
      const key = `${e.host}:${port}`;
      if (!g.ports.some((r) => r.key === key)) g.ports.push(portRow(key, port));
    }
  }
  for (const [key, p] of Object.entries(paths)) {
    const i = key.lastIndexOf(':');
    const h = i > 0 ? key.slice(0, i) : key;
    const port = i > 0 ? key.slice(i + 1) : '';
    const g = group(h);
    if (g.ports.some((r) => r.key === key)) continue;
    g.name = g.name || (p && p.name) || ''; g.direct = g.direct || !!(p && p.direct);
    g.ports.push(portRow(key, port));
  }
  const out = [];
  for (const g of byHost.values()) {
    const sum = (f) => g.ports.reduce((s, r) => s + r[f], 0);
    g.live = sum('live'); g.up = sum('up'); g.down = sum('down');
    g.upRate = sum('upRate'); g.downRate = sum('downRate');
    g.upMoving = g.ports.some((r) => r.upMoving);
    g.downMoving = g.ports.some((r) => r.downMoving);
    g.used = g.up + g.down > 0;
    g.state = g.live > 0 || g.upMoving || g.downMoving ? 'active'
      : g.ports.some((r) => r.recent) ? 'recent'
      : g.used ? 'cooling' : 'idle';
    out.push(g);
  }
  return out;
}

// The path to name under «Сейчас»: the open pipe with the highest byte rate;
// failing that, the port that moved within the last 15 s. Null when nothing
// is live or recent — the header then shows the proxy's memory, dimmed.
function pickHero(hosts) {
  let best = null;
  for (const g of hosts) for (const r of g.ports) {
    const active = r.live > 0 || r.upMoving || r.downMoving;
    if (!active && !r.recent) continue;
    const state = active ? 'active' : 'recent';
    const score = (active ? 1e12 : 0) + (r.live > 0 ? 1e9 : 0) + r.upRate + r.downRate;
    if (!best || score > best.score) best = { score, state, group: g, port: r };
  }
  return best;
}

function describePath({ group, port }) {
  if (group.direct) return 'напрямую';
  return `через ${group.name || group.host}:${port.port}`;
}

function ema(prev, sample) {
  const v = 0.5 * prev + 0.5 * sample;
  return v < RATE_FLOOR ? 0 : v;
}

// Б / КБ / МБ / ГБ, never longer than 7 characters ("1023 МБ", "99.9 МБ"),
// so "↑ " + value always fits the 80 px column at 12 px monospace.
function fmtBytes(n) {
  n = Math.max(0, Number(n) || 0);
  if (n < 1024) return `${Math.round(n)} Б`;
  const kb = n / 1024;
  if (kb < 10) return `${kb.toFixed(1)} КБ`;
  if (kb < 1024) return `${Math.round(kb)} КБ`;
  const mb = kb / 1024;
  if (mb < 10) return `${mb.toFixed(2)} МБ`;
  if (mb < 100) return `${mb.toFixed(1)} МБ`;
  if (mb < 1024) return `${Math.round(mb)} МБ`;
  const gb = mb / 1024;
  return gb < 10 ? `${gb.toFixed(1)} ГБ` : `${Math.round(gb)} ГБ`;
}

function fmtRate(bps) {
  return `${fmtBytes(bps)}/с`;
}

function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

function safeStorage() {
  try { return window.localStorage; } catch (e) { return { getItem: () => null, setItem: () => {} }; }
}
